import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { PackageOpen, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MyReturns } from "@/components/storefront/MyReturns";
import { ReturnDialog, type ReturnableItem } from "@/components/storefront/ReturnDialog";
import { listMyOrdersDetailed } from "@/lib/orders.functions";
import { RETURN_WINDOW_DAYS } from "@/lib/returns.functions";
import { formatPrice } from "@/lib/format";

export const Route = createFileRoute("/_authenticated/account/returns")({
  head: () => ({ meta: [{ title: "Οι επιστροφές μου — MrPanda.gr" }] }),
  component: ReturnsPage,
});

type OrderItem = { id: string; name: string; quantity: number; unit_price: number | null };
type Order = {
  id: string;
  order_number: string | null;
  external_id: string | null;
  status: string;
  created_at: string;
  delivered_at: string | null;
  total: number;
  items: OrderItem[];
};

function isEligible(o: Order): boolean {
  if (o.status === "cancelled") return false;
  const base = new Date(o.delivered_at ?? o.created_at);
  const days = (Date.now() - base.getTime()) / 86_400_000;
  return Number.isFinite(days) && days <= RETURN_WINDOW_DAYS;
}

function ReturnsPage() {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string>("");
  const [returnOrder, setReturnOrder] = useState<Order | null>(null);

  const fn = useServerFn(listMyOrdersDetailed);
  const { data, isLoading } = useQuery({
    queryKey: ["my-orders-detailed"],
    queryFn: () => fn(),
    enabled: pickerOpen,
  });
  const eligible = ((data?.orders ?? []) as Order[]).filter(isEligible);

  const openPicker = () => {
    setSelectedId("");
    setPickerOpen(true);
  };
  const confirmPick = () => {
    const o = eligible.find((x) => x.id === selectedId);
    if (!o) return;
    setReturnOrder(o);
    setPickerOpen(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold">Οι επιστροφές μου</h1>
          <p className="text-sm text-muted-foreground">
            Για νέα επιστροφή, επίλεξε μια παραγγελία εντός {RETURN_WINDOW_DAYS} ημερών.
          </p>
        </div>
        <Button onClick={openPicker}>
          <PackageOpen className="w-4 h-4 mr-2" />
          Δημιουργία επιστροφής
        </Button>
      </div>
      <MyReturns alwaysShow />

      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PackageOpen className="w-5 h-5 text-primary" />
              Επίλεξε παραγγελία
            </DialogTitle>
            <DialogDescription>
              Εμφανίζονται μόνο παραγγελίες εντός των {RETURN_WINDOW_DAYS} ημερών.
            </DialogDescription>
          </DialogHeader>

          {isLoading ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              Φόρτωση παραγγελιών...
            </div>
          ) : eligible.length === 0 ? (
            <div className="rounded-xl bg-muted/50 p-6 text-center text-sm text-muted-foreground">
              Δεν υπάρχει παραγγελία επιλέξιμη για επιστροφή αυτή τη στιγμή.
            </div>
          ) : (
            <div className="space-y-2 py-2">
              <label className="text-sm font-medium">Παραγγελία</label>
              <Select value={selectedId} onValueChange={setSelectedId}>
                <SelectTrigger>
                  <SelectValue placeholder="Διάλεξε παραγγελία..." />
                </SelectTrigger>
                <SelectContent>
                  {eligible.map((o) => {
                    const label = o.order_number ?? o.external_id ?? o.id.slice(0, 8);
                    const date = new Date(o.created_at).toLocaleDateString("el-GR");
                    return (
                      <SelectItem key={o.id} value={o.id}>
                        {label} — {date} — {formatPrice(Number(o.total))}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setPickerOpen(false)}>
              Άκυρο
            </Button>
            <Button onClick={confirmPick} disabled={!selectedId}>
              Συνέχεια
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {returnOrder && (
        <ReturnDialog
          open={!!returnOrder}
          onOpenChange={(v) => !v && setReturnOrder(null)}
          orderId={returnOrder.id}
          orderNumber={returnOrder.order_number ?? returnOrder.external_id}
          items={
            returnOrder.items.map((it) => ({
              id: it.id,
              name: it.name,
              quantity: it.quantity,
              unitPrice: it.unit_price ?? null,
            })) as ReturnableItem[]
          }
        />
      )}
    </div>
  );
}